import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const SHORT_ACTIVATE_DELAY = 0;
const LONG_ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    onDocumentReady(this.#preloadImages);
    this.#initNestedTabs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (this.#state.activeItem) {
      this.#shortDebouncedActivateHandler(event);
    } else {
      this.#longDebouncedActivateHandler(event);
    }
  };

  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);
    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    // Handle regular submenu vs nested mega menu
    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;
    let submenuHeight = 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
      submenuHeight = overflowMenuHeight;
    }

    // Handle nested mega menu activation
    const menuHandle = item?.getAttribute('data-menu-handle');
    const allNestedMenus = this.querySelectorAll('.nested-mega-menu-wrapper');
    let activeNestedMenu = null;

    // Deactivate all nested menus first
    allNestedMenus.forEach((menu) => menu.classList.remove('active'));

    if (menuHandle) {
      const matchingWrapper = this.querySelector(`.nested-mega-menu-wrapper[data-menu-handle="${menuHandle}"]`);
      if (matchingWrapper) {
        matchingWrapper.classList.add('active');
        activeNestedMenu = matchingWrapper;
        
        // Use nested menu height instead of regular submenu
        submenu = matchingWrapper;
        submenuHeight = matchingWrapper.offsetHeight;
      }
    }

    // Calculate final height (regular submenu or nested menu)
    if (submenu && !activeNestedMenu) {
      submenuHeight = Math.max(submenu.offsetHeight, overflowMenuHeight);
    }

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
  };

  #shortDebouncedActivateHandler = debounce(this.#activateHandler, SHORT_ACTIVATE_DELAY);
  #longDebouncedActivateHandler = debounce(this.#activateHandler, LONG_ACTIVATE_DELAY);

  deactivate(event) {
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.dataset.overflowExpanded = 'false';

    // Deactivate all nested mega menus
    const allNestedMenus = this.querySelectorAll('.nested-mega-menu-wrapper');
    allNestedMenus.forEach((menu) => menu.classList.remove('active'));

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #initNestedTabs() {
    const nestedMenus = this.querySelectorAll('.nested-mega-menu-wrapper');

    nestedMenus.forEach((wrapper) => {
      const tabLinks = wrapper.querySelectorAll('.tab-link');
      const submenus = wrapper.querySelectorAll('.submenu');

      tabLinks.forEach((tab, index) => {
        tab.addEventListener('mouseenter', () => {
          // Remove active class from all tabs and submenus in this wrapper
          tabLinks.forEach(el => el.classList.remove('active'));
          submenus.forEach(el => el.classList.remove('active'));

          // Activate current tab and matching submenu
          tab.classList.add('active');
          const matchingSubmenu = wrapper.querySelector(`.submenu[data-content="${index}"]`);
          if (matchingSubmenu) {
            matchingSubmenu.classList.add('active');
            
            // Recalculate height when switching tabs
            const currentHeight = wrapper.offsetHeight;
            this.style.setProperty('--submenu-height', `${currentHeight}px`);
          }
        }, { signal: this.#abortController.signal });
      });

      // Add hover handling to keep the nested menu active
      wrapper.addEventListener('pointerenter', () => {
        this.#debouncedDeactivate.cancel();
      }, { signal: this.#abortController.signal });

      wrapper.addEventListener('pointerleave', () => {
        this.#debouncedDeactivate();
      }, { signal: this.#abortController.signal });
    });
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[role="menuitem"]');
}

/**
 * Find the closest submenu (excluding nested mega menus).
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]:not(.nested-mega-menu-wrapper)');
  return submenu instanceof HTMLElement ? submenu : null;
}